import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { BookingManager, esitoAnnullamento } from '../../lib/booking-manager';
import { LoginManager } from '../../core/login-manager';
import { settings } from '../../config/settings';
import {
    formatImporto, formatIntervallo, nomeUtente, optValue, parseSql, refId, refLabel, umanizza
} from '../../lib/format';
import { isAdmin, isAdminGlobale } from '../../lib/ruoli';
import {
    caricaTutti, ChiudiModale, OpenLink, Pager, usePaginazione, classePillStato
} from '../admin/common';

/** Stati che occupano ancora il calendario, come in BookingEngine. */
const STATI_ATTIVI = ['confermata', 'conclusa'];

/** Le due viste della pagina, nell'ordine in cui compaiono sulla barra. */
const VISTE = [
    { id: 'prossime', label: 'In programma' },
    { id: 'storico', label: 'Storico' }
];

/** Valori neutri dei filtri: qui nessun filtro e' attivo. */
const FILTRI_VUOTI = {
    ricerca: '', sala: '0', condominio: '0', stato: 'tutti', anno: 'tutti', pagamento: 'tutti'
};

/** Saldata o no conta solo per cio' che occupa il calendario: il resto non si incassa. */
function daIncassare(p) {
    return STATI_ATTIVI.includes(optValue(p.stato)) && parseInt(p.pagato, 10) !== 1;
}

/**
 * Voci di una tendina ricavate dalle prenotazioni gia' in mano, non da una
 * chiamata a parte: filtrare per una sala che non compare in elenco non
 * servirebbe a nulla, e l'elenco lo abbiamo gia'.
 */
function opzioniDa(righe, estrai) {
    const visti = new Map();
    righe.forEach(p => {
        const v = estrai(p);
        if (v && v.value && !visti.has(String(v.value))) visti.set(String(v.value), v);
    });
    return Array.from(visti.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Elenco prenotazioni future e storico, con possibilita' di annullamento
 * entro i termini previsti.
 *
 * Le due viste stanno su tab separate invece che impilate: lo storico cresce
 * senza limite e, messo sotto, allontanava dallo schermo proprio la parte che
 * serve piu' spesso, cioe' cio' che deve ancora accadere.
 *
 * Il condomino vede le proprie; l'amministratore vede tutto quello che
 * l'API gli restituisce - il condominio se e' di livello 1, ogni condominio
 * se e' globale - perche' l'elenco gli serve anche come vista operativa.
 *
 * All'amministratore la pagina fa anche da registro degli incassi: su ogni
 * scheda attiva dice se e' saldata, c'e' un filtro per il pagamento e, sopra
 * l'elenco, i totali della vista aperta. Al condomino no: il saldo si
 * sistema fuori dalla piattaforma e mostrarglielo prometterebbe un conto che
 * non c'e'. A lui resta l'importo, che e' un dato della prenotazione.
 *
 * L'annullamento e' delegato a `cancel_booking`: e' il backend a decidere se
 * il preavviso e' rispettato e a segnalare l'eventuale addebito.
 */
export default function MiePrenotazioni() {
    const [userInfo] = useState(() => LoginManager.getUserInfo());
    const amministratore = isAdmin(userInfo);
    const globale = isAdminGlobale(userInfo);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);

    const [vista, setVista] = useState('prossime');
    const [filtri, setFiltri] = useState(FILTRI_VUOTI);
    const [daAnnullare, setDaAnnullare] = useState(null);
    const [motivo, setMotivo] = useState('');
    const [working, setWorking] = useState(false);

    const idUtente = parseInt(userInfo && userInfo.id, 10) || 0;

    const carica = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const res = await caricaTutti('prenotazione', 'inizio', 'desc');
            if (res && res.success === 1) {
                // l'API filtra gia' per livello: al condomino torna solo cio'
                // che ha prenotato, all'amministratore l'intero perimetro.
                const righe = res.body || [];
                setItems(amministratore ? righe : righe.filter(p => refId(p.author) === idUtente));
            }
            else {
                setErrore((res && res.message) || 'Impossibile caricare le prenotazioni.');
            }
        }
        catch (err) {
            setErrore('Impossibile contattare il server.');
        }
        finally {
            setLoading(false);
        }
    }, [idUtente, amministratore]);

    useEffect(() => {
        carica();
    }, [carica]);

    const { prossime, storico } = useMemo(() => {
        const adesso = new Date();
        const a = [];
        const b = [];
        items.forEach(p => {
            const inizio = parseSql(p.inizio);
            const attiva = STATI_ATTIVI.includes(optValue(p.stato));
            if (attiva && inizio && inizio >= adesso) a.push(p);
            else b.push(p);
        });
        a.sort((x, y) => String(x.inizio).localeCompare(String(y.inizio)));
        return { prossime: a, storico: b };
    }, [items]);

    /* ---------------------------------------------------------- filtri -- */

    function cambia(campo, valore) {
        setFiltri(prev => ({ ...prev, [campo]: valore }));
    }

    function azzera() {
        setFiltri(FILTRI_VUOTI);
    }

    const opzioniSala = useMemo(
        () => opzioniDa(items, p => ({ value: refId(p.idSala), label: refLabel(p.idSala, ['nome']) })),
        [items]
    );
    const opzioniCondominio = useMemo(
        () => opzioniDa(items, p => ({
            value: refId(p.idUserGroup),
            label: refLabel(p.idUserGroup, ['name'], 'Condominio')
        })),
        [items]
    );
    /* Stato e anno riguardano solo l'archivio: in programma sono tutte attive
       e tutte future, due tendine che risponderebbero sempre allo stesso modo. */
    const opzioniStato = useMemo(
        () => opzioniDa(storico, p => ({ value: optValue(p.stato), label: umanizza(optValue(p.stato)) })),
        [storico]
    );
    const opzioniAnno = useMemo(() => {
        const anni = opzioniDa(storico, p => {
            const anno = String(p.inizio || '').substring(0, 4);
            return anno ? { value: anno, label: anno } : null;
        });
        return anni.reverse(); // il piu' recente per primo: e' quello che si cerca
    }, [storico]);

    /** Filtri che valgono su entrambe le viste. */
    const passaComuni = useCallback((p) => {
        if (filtri.sala !== '0' && refId(p.idSala) !== parseInt(filtri.sala, 10)) return false;
        if (filtri.condominio !== '0' && refId(p.idUserGroup) !== parseInt(filtri.condominio, 10)) return false;
        if (filtri.pagamento !== 'tutti') {
            if (!STATI_ATTIVI.includes(optValue(p.stato))) return false;
            if ((filtri.pagamento === 'da_incassare') !== daIncassare(p)) return false;
        }

        const q = filtri.ricerca.trim().toLowerCase();
        if (!q) return true;
        const blob = [
            p.motivo, p.note, nomeUtente(p.author),
            refLabel(p.idSala, ['nome'], ''), refLabel(p.idUserGroup, ['name'], '')
        ].join(' ').toLowerCase();
        return blob.includes(q);
    }, [filtri]);

    const prossimeVisibili = useMemo(() => prossime.filter(passaComuni), [prossime, passaComuni]);
    const storicoVisibile = useMemo(() => storico.filter(p => {
        if (!passaComuni(p)) return false;
        if (filtri.stato !== 'tutti' && optValue(p.stato) !== filtri.stato) return false;
        if (filtri.anno !== 'tutti' && String(p.inizio || '').substring(0, 4) !== filtri.anno) return false;
        return true;
    }), [storico, passaComuni, filtri.stato, filtri.anno]);

    const filtriAttivi = Object.keys(FILTRI_VUOTI).some(k => filtri[k] !== FILTRI_VUOTI[k]);
    const suStorico = vista === 'storico';

    /* Una paginazione sola per le due viste, non una per ciascuna: cambiando
       tab si riparte dalla prima pagina, che e' anche cio' che serve - si
       passa allo storico per cercare, non per riprendere da dove si era. */
    const righeVista = suStorico ? storicoVisibile : prossimeVisibili;
    const pag = usePaginazione(
        righeVista,
        `${vista}|${Object.keys(FILTRI_VUOTI).map(k => filtri[k]).join('|')}`
    );

    /* I totali guardano tutte le righe filtrate della vista, non la pagina:
       sommare quello che si ha davanti darebbe un incasso diverso a ogni
       "successiva". */
    const totali = useMemo(() => {
        const attive = righeVista.filter(p => STATI_ATTIVI.includes(optValue(p.stato)));
        const somma = righe => righe.reduce((acc, p) => acc + (parseFloat(p.importo) || 0), 0);
        return {
            attive: attive.length,
            incassato: somma(attive.filter(p => !daIncassare(p))),
            daIncassare: somma(attive.filter(daIncassare))
        };
    }, [righeVista]);

    /** Ore mancanti all'inizio: sotto il termine della sala scatta l'addebito. */
    function fuoriTermine(p) {
        const inizio = parseSql(p.inizio);
        if (!inizio) return false;
        const soglia = p.idSala && p.idSala.annullamento_ore
            ? parseInt(p.idSala.annullamento_ore, 10)
            : settings.booking.cancelDeadlineHours;
        return (inizio.getTime() - Date.now()) / 3600000 < soglia;
    }

    async function annulla() {
        setWorking(true);
        try {
            const res = await BookingManager.cancelBooking(daAnnullare.id, motivo || null);
            if (res && res.success === 1) {
                toast.success(res.message || 'Prenotazione annullata.', { theme: 'colored' });
                setDaAnnullare(null);
                setMotivo('');
                carica();
            }
            else {
                toast.error((res && res.message) || 'Annullamento non riuscito.', { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    if (loading) {
        return (
            <main className="lv-shell lv-stack">
                <section className="lv-card lv-card-pad">
                    <div className="lv-empty">
                        <div className="lv-empty-icon">
                            <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
                        </div>
                        <p className="mb-0">Caricamento in corso…</p>
                    </div>
                </section>
            </main>
        );
    }

    /**
     * Elenco vuoto. Con i filtri attivi il messaggio deve dire che la colpa e'
     * loro, altrimenti sembra che non ci sia nulla da vedere. Il modo di
     * toglierli e' li' sopra, nella barra: non si ripete anche qui.
     */
    function renderVuoto(icona, testo, testoFiltrato) {
        return (
            <div className="lv-empty">
                <div className="lv-empty-icon">
                    <FontAwesomeIcon icon={['fas', icona]} />
                </div>
                <p className="mb-0">{filtriAttivi ? testoFiltrato : testo}</p>
            </div>
        );
    }

    /**
     * Una prenotazione, in programma o archiviata: la scheda e' la stessa.
     * Lo storico era una tabella, ma vista da mobile - dove questa pagina si
     * guarda quasi sempre - diventava una scheda a sua volta, solo con altri
     * spazi e un altro ordine: due modi di disegnare la stessa cosa, e il
     * passaggio da una tab all'altra sembrava un cambio di pagina.
     *
     * Cambia solo cio' che di fatto cambia: l'annullamento si puo' chiedere
     * finche' la prenotazione e' davanti a noi, e il motivo per cui e' saltata
     * ha senso leggerlo solo dopo.
     */
    function renderScheda(p) {
        const stato = optValue(p.stato);
        return (
            <article key={p.id} className="lv-booking">
                <div className="lv-booking-main">
                    <p className="lv-eyebrow">
                        {refLabel(p.idSala, ['nome'])}
                        {globale && ` · ${refLabel(p.idUserGroup, ['name'], 'Condominio')}`}
                    </p>
                    <h3 className="lv-title">{formatIntervallo(p.inizio, p.fine)}</h3>
                    <p className="lv-subtitle mb-0">
                        {amministratore && `${nomeUtente(p.author)} — `}
                        {p.motivo}
                    </p>
                    {stato === 'annullata' && p.motivo_annullamento && (
                        <p className="lv-subtitle lv-cell-sub mb-0">Annullata: {p.motivo_annullamento}</p>
                    )}
                </div>
                <div className="lv-booking-meta">
                    <span className={`lv-pill ${classePillStato(stato)}`}>{umanizza(stato)}</span>
                    <span className="lv-booking-price">{formatImporto(p.importo)}</span>
                    {stato === 'annullata' && esitoAnnullamento(p) && (
                        <span className={`lv-pill ${esitoAnnullamento(p).dovuto ? 'lv-pill-warn' : 'lv-pill-ok'}`}>
                            {esitoAnnullamento(p).dovuto ? 'Importo dovuto' : 'Nessun addebito'}
                        </span>
                    )}
                    {amministratore && STATI_ATTIVI.includes(stato) && (
                        <span className={`lv-pill ${daIncassare(p) ? 'lv-pill-warn' : 'lv-pill-ok'}`}>
                            {daIncassare(p) ? 'Da incassare' : 'Pagato'}
                        </span>
                    )}
                </div>
                <div className="lv-booking-actions">
                    <OpenLink to={`/prenotazioni/${p.id}`} label="Dettaglio" />
                    {!suStorico && (
                        <button
                            type="button"
                            className="lv-btn lv-btn-ghost lv-btn-sm lv-btn-danger-ghost"
                            onClick={() => { setDaAnnullare(p); setMotivo(''); }}
                        >
                            Annulla
                        </button>
                    )}
                </div>
            </article>
        );
    }

    /** L'elenco della vista aperta: stesse schede, vuoti diversi. */
    function renderElenco() {
        if (righeVista.length === 0) {
            return suStorico
                ? renderVuoto(
                    'clock-rotate-left',
                    'Nello storico non c\'e\' ancora niente.',
                    'Nello storico non c\'e\' niente che corrisponda ai filtri.'
                )
                : renderVuoto(
                    'calendar-days',
                    amministratore ? 'Nessuna prenotazione in programma.' : 'Non hai prenotazioni in programma.',
                    'Nessuna prenotazione in programma corrisponde ai filtri.'
                );
        }
        return <div className="lv-booking-list">{pag.visibili.map(renderScheda)}</div>;
    }

    /* I contatori sulle tab seguono i filtri, non la pagina: contano quanto
       c'e' di la', che e' il motivo per cui si guarda un contatore. */
    const conteggi = { prossime: prossimeVisibili.length, storico: storicoVisibile.length };

    return (
        <main className="lv-shell lv-stack">
            <nav className="lv-tabs lv-tabs-standalone" role="tablist" aria-label="Viste prenotazioni">
                {VISTE.map(v => (
                    <button
                        key={v.id}
                        type="button"
                        role="tab"
                        id={`tab-${v.id}`}
                        aria-selected={vista === v.id}
                        aria-controls="pannello-prenotazioni"
                        className={`lv-tab${vista === v.id ? ' active' : ''}`}
                        onClick={() => setVista(v.id)}
                    >
                        {v.label}
                        <span className="lv-tab-count">{conteggi[v.id]}</span>
                    </button>
                ))}
            </nav>

            <section
                className="lv-card lv-card-pad"
                id="pannello-prenotazioni"
                role="tabpanel"
                aria-labelledby={`tab-${vista}`}
            >
                <div className="lv-card-head">
                    <div>
                        <p className="lv-eyebrow">
                            {suStorico ? 'Archivio' : (amministratore ? 'Registro' : 'Area personale')}
                        </p>
                        <h2 className="lv-title lv-title-lg">
                            {suStorico
                                ? 'Storico'
                                : (amministratore ? 'Prenotazioni in programma' : 'Le mie prenotazioni')}
                        </h2>
                    </div>
                    <Link to="/prenota" className="lv-btn lv-btn-primary lv-btn-pill">
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                        Nuova prenotazione
                    </Link>
                </div>

                {/* con una prenotazione sola non c'e' niente da filtrare */}
                {!errore && items.length > 1 && (
                    <div className="lv-toolbar">
                        <input
                            type="search"
                            className="lv-input"
                            aria-label="Cerca"
                            placeholder={amministratore
                                ? 'Cerca per condomino, sala o motivo…'
                                : 'Cerca per sala o motivo…'}
                            value={filtri.ricerca}
                            onChange={e => cambia('ricerca', e.target.value)}
                        />
                        {opzioniSala.length > 1 && (
                            <select
                                className="lv-select"
                                aria-label="Sala"
                                value={filtri.sala}
                                onChange={e => cambia('sala', e.target.value)}
                            >
                                <option value="0">Tutte le sale</option>
                                {opzioniSala.map(o => (
                                    <option key={o.value} value={String(o.value)}>{o.label}</option>
                                ))}
                            </select>
                        )}
                        {globale && opzioniCondominio.length > 1 && (
                            <select
                                className="lv-select"
                                aria-label="Condominio"
                                value={filtri.condominio}
                                onChange={e => cambia('condominio', e.target.value)}
                            >
                                <option value="0">Tutti i condomini</option>
                                {opzioniCondominio.map(o => (
                                    <option key={o.value} value={String(o.value)}>{o.label}</option>
                                ))}
                            </select>
                        )}
                        {suStorico && opzioniStato.length > 1 && (
                            <select
                                className="lv-select"
                                aria-label="Stato"
                                value={filtri.stato}
                                onChange={e => cambia('stato', e.target.value)}
                            >
                                <option value="tutti">Tutti gli stati</option>
                                {opzioniStato.map(o => (
                                    <option key={o.value} value={String(o.value)}>{o.label}</option>
                                ))}
                            </select>
                        )}
                        {suStorico && opzioniAnno.length > 1 && (
                            <select
                                className="lv-select"
                                aria-label="Anno"
                                value={filtri.anno}
                                onChange={e => cambia('anno', e.target.value)}
                            >
                                <option value="tutti">Tutti gli anni</option>
                                {opzioniAnno.map(o => (
                                    <option key={o.value} value={String(o.value)}>{o.label}</option>
                                ))}
                            </select>
                        )}
                        {amministratore && (
                            <select
                                className="lv-select"
                                aria-label="Pagamento"
                                value={filtri.pagamento}
                                onChange={e => cambia('pagamento', e.target.value)}
                            >
                                <option value="tutti">Tutti i pagamenti</option>
                                <option value="pagato">Pagato</option>
                                <option value="da_incassare">Da incassare</option>
                            </select>
                        )}
                        {filtriAttivi && (
                            <button type="button" className="lv-btn lv-btn-ghost" onClick={azzera}>
                                Azzera i filtri
                            </button>
                        )}
                    </div>
                )}

                {amministratore && !errore && totali.attive > 0 && (
                    <div className="lv-stats">
                        <div className="lv-stat">
                            <span className="lv-stat-label">Prenotazioni attive</span>
                            <span className="lv-stat-value">{totali.attive}</span>
                        </div>
                        <div className="lv-stat">
                            <span className="lv-stat-label">Incassato</span>
                            <span className="lv-stat-value">{formatImporto(totali.incassato)}</span>
                        </div>
                        <div className="lv-stat">
                            <span className="lv-stat-label">Da incassare</span>
                            <span className="lv-stat-value">{formatImporto(totali.daIncassare)}</span>
                        </div>
                    </div>
                )}

                {errore ? (
                    <div className="lv-note lv-note-error">
                        <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className="me-2" />
                        {errore}
                        <button type="button" className="lv-btn lv-btn-ghost lv-btn-sm ms-3" onClick={carica}>
                            Riprova
                        </button>
                    </div>
                ) : renderElenco()}

                {!errore && <Pager stato={pag} etichetta="Pagine delle prenotazioni" />}
            </section>

            {daAnnullare && (
                <div className="lv-modal-backdrop" role="dialog" aria-modal="true">
                    <div className="lv-modal">
                        <div className="lv-modal-head">
                            <div className="lv-modal-head-text">
                                <p className="lv-eyebrow">{refLabel(daAnnullare.idSala, ['nome'])}</p>
                                <h3 className="lv-title">Annullare la prenotazione?</h3>
                            </div>
                            <ChiudiModale onClose={() => setDaAnnullare(null)} disabled={working} />
                        </div>
                        <div className="lv-modal-body">
                            <p>{formatIntervallo(daAnnullare.inizio, daAnnullare.fine)}</p>
                            {fuoriTermine(daAnnullare) && (
                                <div className="lv-note lv-note-warn">
                                    <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className="me-2" />
                                    Sei oltre il termine di preavviso: l&apos;importo potrebbe restare dovuto.
                                </div>
                            )}
                            <label className="lv-field mt-3">
                                <span className="lv-label">Motivo (facoltativo)</span>
                                <textarea
                                    className="lv-input"
                                    rows={3}
                                    value={motivo}
                                    onChange={e => setMotivo(e.target.value)}
                                />
                            </label>
                        </div>
                        <div className="lv-modal-foot">
                            <button
                                type="button"
                                className="lv-btn lv-btn-danger"
                                onClick={annulla}
                                disabled={working}
                            >
                                {working && <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />}
                                Annulla prenotazione
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
